/**
 * 처방 마감일 설정 — 월별로 처방등록년월이 넘어가는 기준일을 지정한다.
 * 제약사마다 처방 이미지를 주는 날짜가 달라 운영팀이 매달 마감을 조정한다.
 * 마감일 당일까지 등록 → 그 달, 다음날 0시부터 → 다음 달 (미설정 달은 10일).
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { CalendarClock, Info } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { CardContent } from '@/shared/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/ui/form';
import {
  popupPrimaryButtonClassName,
  popupSecondaryButtonClassName,
} from '@/shared/components/ui/popup-styles';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { getPrescriptionDeadlineErrorMessage } from '@/entities/prescription/api/prescription-registration-deadline.api';
import {
  usePrescriptionRegistrationDeadlineMutations,
  usePrescriptionRegistrationDeadlines,
} from '@/entities/prescription/hooks/usePrescriptionRegistrationDeadlines';
import { useConfirmDialog } from '@/shared/components/ConfirmDialogProvider';
import { DataCard } from '@/shared/components/DataCard';
import { DatePicker } from '@/shared/components/DatePicker';
import { EmptyState } from '@/shared/components/EmptyState';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import { formatDateDisplay, parseDateValue } from '@/shared/lib/datePickerUtils';
import { handleApiError, useToast } from '@/shared/utils/toast';
import {
  buildDeadlineMonthRows,
  buildDeadlineYearOptions,
  DEFAULT_PRESCRIPTION_DEADLINE_DAY,
  formatYearMonthLabel,
  getSelectableDeadlineRange,
  toTodayValue,
  type DeadlineMonthRow,
  type DeadlineMonthStatus,
} from './prescriptionRegistrationDeadlineUtils';

const STATUS_BADGE: Record<
  DeadlineMonthStatus,
  { label: string; variant: 'secondary' | 'success' | 'outline' }
> = {
  PASSED: { label: '마감', variant: 'secondary' },
  CURRENT: { label: '진행 중', variant: 'success' },
  UPCOMING: { label: '예정', variant: 'outline' },
};

const deadlineSchema = z.object({
  deadlineDate: z.string().min(1, '마감일을 선택해주세요'),
});

type DeadlineFormValues = z.infer<typeof deadlineSchema>;

interface DeadlineDialogProps {
  /** null이면 닫힘 */
  row: DeadlineMonthRow | null;
  today: string;
  submitting: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (row: DeadlineMonthRow, deadlineDate: string) => void;
}

const DeadlineDialog: React.FC<DeadlineDialogProps> = ({
  row,
  today,
  submitting,
  onOpenChange,
  onSubmit,
}) => {
  const form = useForm<DeadlineFormValues>({
    resolver: zodResolver(deadlineSchema),
    defaultValues: { deadlineDate: '' },
  });
  const range = row ? getSelectableDeadlineRange(row.yearMonth, today) : null;

  const fromDate = range ? parseDateValue(range.from) : undefined;
  const toDate = range ? parseDateValue(range.to) : undefined;
  // 지금 적용 중인 마감일을 다시 고르면 바뀌는 게 없으므로 선택에서 뺀다
  const currentDeadline = row ? parseDateValue(row.deadlineDate) : undefined;

  useEffect(() => {
    if (row) form.reset({ deadlineDate: '' });
  }, [row, form]);

  const handleSubmit = (values: DeadlineFormValues) => {
    if (!row || !range) return;
    if (values.deadlineDate < range.from || values.deadlineDate > range.to) {
      form.setError('deadlineDate', {
        message: '해당 월 안에서 오늘 이후 날짜를 선택해주세요',
      });
      return;
    }
    if (values.deadlineDate === row.deadlineDate) {
      form.setError('deadlineDate', {
        message: '현재 적용 마감일과 다른 날짜를 선택해주세요',
      });
      return;
    }
    onSubmit(row, values.deadlineDate);
  };

  return (
    <Dialog open={row !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {row ? formatYearMonthLabel(row.yearMonth) : ''} 처방 마감일 설정
          </DialogTitle>
          <DialogDescription>
            마감일 당일까지 등록된 처방은 이 달, 다음날 0시부터는 다음 달
            처방으로 등록됩니다. 이미 저장된 처방은 다시 계산하지 않습니다.
          </DialogDescription>
          {row ? (
            <p className="text-sm text-muted-foreground">
              현재 적용 마감일:{' '}
              <span className="font-medium text-foreground">
                {formatDateDisplay(row.deadlineDate)}
              </span>
              {row.isDefault ? ` (기본 ${DEFAULT_PRESCRIPTION_DEADLINE_DAY}일)` : ''}
            </p>
          ) : null}
        </DialogHeader>
        <Form {...form}>
          <form
            id="prescription-deadline-form"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormField
              control={form.control}
              name="deadlineDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>마감일</FormLabel>
                  <FormControl>
                    <DatePicker
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="마감일 선택"
                      captionLayout="label"
                      defaultMonth={fromDate}
                      disabledDays={
                        fromDate && toDate
                          ? [
                              { before: fromDate },
                              { after: toDate },
                              ...(currentDeadline ? [currentDeadline] : []),
                            ]
                          : undefined
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            className={popupSecondaryButtonClassName}
            onClick={() => onOpenChange(false)}
          >
            취소
          </Button>
          <Button
            type="submit"
            form="prescription-deadline-form"
            className={popupPrimaryButtonClassName}
            loading={submitting}
          >
            저장
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const PrescriptionRegistrationDeadlinePage: React.FC = () => {
  const toast = useToast();
  const confirm = useConfirmDialog();
  const today = toTodayValue();
  const currentYear = Number(today.slice(0, 4));
  const [year, setYear] = useState(currentYear);
  const [editingRow, setEditingRow] = useState<DeadlineMonthRow | null>(null);

  const { data, isLoading, isError, refetch } =
    usePrescriptionRegistrationDeadlines(year);
  const { create, update, remove } =
    usePrescriptionRegistrationDeadlineMutations();
  // 상태 판정은 1월부터 순서대로 해야 하므로 계산 후 최신 월이 위로 오게 뒤집는다
  const rows = buildDeadlineMonthRows(year, data ?? [], today).reverse();

  const showError = (error: unknown) => {
    const message = getPrescriptionDeadlineErrorMessage(error);
    if (message) toast.error(message);
    else handleApiError(error, toast);
  };

  const handleSave = async (row: DeadlineMonthRow, deadlineDate: string) => {
    try {
      if (row.isDefault) {
        await create.mutateAsync({ yearMonth: row.yearMonth, deadlineDate });
      } else {
        await update.mutateAsync({ yearMonth: row.yearMonth, deadlineDate });
      }
      toast.success(
        `${formatYearMonthLabel(row.yearMonth)} 마감일을 ${formatDateDisplay(deadlineDate)}로 저장했습니다.`,
      );
      setEditingRow(null);
    } catch (error) {
      showError(error);
    }
  };

  const handleDelete = async (row: DeadlineMonthRow) => {
    const ok = await confirm({
      title: '마감일 초기화',
      description: `${formatYearMonthLabel(row.yearMonth)} 마감일 설정을 지우고 기본 ${DEFAULT_PRESCRIPTION_DEADLINE_DAY}일로 되돌립니다.`,
      confirmLabel: '초기화',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      await remove.mutateAsync(row.yearMonth);
      toast.success(
        `${formatYearMonthLabel(row.yearMonth)} 마감일을 기본 ${DEFAULT_PRESCRIPTION_DEADLINE_DAY}일로 되돌렸습니다.`,
      );
    } catch (error) {
      showError(error);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="처방 마감일 설정"
        description="월별로 처방등록년월이 다음 달로 넘어가는 기준일을 지정합니다."
        icon={CalendarClock}
      />

      <Alert>
        <Info className="size-4" />
        <AlertDescription>
          마감일 <strong>당일까지</strong> 등록된 처방은 그 달, 다음날
          0시부터는 다음 달 처방등록년월로 저장됩니다. 정산 년월은
          처방등록년월 + 1개월입니다. 설정하지 않은 달은 기본{' '}
          {DEFAULT_PRESCRIPTION_DEADLINE_DAY}일이 적용되며, 오늘 이후 날짜로만
          지정·변경할 수 있습니다. 이미 저장된 처방은 다시 계산하지 않습니다.
        </AlertDescription>
      </Alert>

      <DataCard>
        {/* Radix Select가 숨은 native select를 형제로 렌더해 space-y 간격이 먹지 않으므로 gap을 쓴다 */}
        <CardContent className="flex flex-col gap-6">
          <Select
            value={String(year)}
            onValueChange={(value) => setYear(Number(value))}
          >
            <SelectTrigger className="w-32" aria-label="연도 선택">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {buildDeadlineYearOptions(currentYear).map((option) => (
                <SelectItem key={option} value={String(option)}>
                  {option}년
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {isLoading ? (
            <LoadingSpinner tip="처방 마감일을 불러오는 중..." />
          ) : isError ? (
            // 조회 실패 시 기본 10일 표를 보여주면 설정이 없는 것으로 오해하므로 표를 숨긴다
            <EmptyState
              description="처방 마감일을 불러오지 못했습니다."
              action={
                <Button variant="outline" onClick={() => refetch()}>
                  다시 시도
                </Button>
              }
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>처방등록년월</TableHead>
                  <TableHead>적용 마감일</TableHead>
                  <TableHead>구분</TableHead>
                  <TableHead>상태</TableHead>
                  <TableHead>마지막 수정</TableHead>
                  <TableHead className="text-right">관리</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const status = STATUS_BADGE[row.status];
                  const hasAction =
                    row.canCreate || row.canUpdate || row.canDelete;
                  return (
                    <TableRow key={row.yearMonth}>
                      <TableCell className="font-medium">
                        {formatYearMonthLabel(row.yearMonth)}
                      </TableCell>
                      <TableCell>{formatDateDisplay(row.deadlineDate)}</TableCell>
                      <TableCell>
                        {row.isDefault ? (
                          <Badge variant="secondary">
                            기본 ({DEFAULT_PRESCRIPTION_DEADLINE_DAY}일)
                          </Badge>
                        ) : (
                          <Badge>설정</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={status.variant}>{status.label}</Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {row.updatedAt ?? '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <MenuWriteGate fallback="-">
                          {hasAction ? (
                            <div className="flex justify-end gap-2">
                              {/* 설정 버튼이 행마다 같은 우측 위치에 오도록 초기화를 앞에 둔다 */}
                              {row.canDelete ? (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-destructive"
                                  onClick={() => handleDelete(row)}
                                >
                                  초기화
                                </Button>
                              ) : null}
                              {/* 추가(POST)·수정(PUT)은 저장 시 isDefault로 갈리므로 버튼은 하나로 둔다 */}
                              {row.canCreate || row.canUpdate ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setEditingRow(row)}
                                >
                                  설정
                                </Button>
                              ) : null}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </MenuWriteGate>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </DataCard>

      <DeadlineDialog
        row={editingRow}
        today={today}
        submitting={create.isPending || update.isPending}
        onOpenChange={(open) => {
          if (!open) setEditingRow(null);
        }}
        onSubmit={handleSave}
      />
    </div>
  );
};
